/* WhatsApp-before-checkout gate.
 *
 * When the admin enables it (a metaobject flag the app writes), a guest who has
 * not yet given a WhatsApp number is asked for one before leaving for Shopify
 * checkout. The number is stored as a cart attribute ("WhatsApp Number") so it
 * travels to the order — where a Zoho CRM lead can later be created from it.
 *
 * Nothing happens when: the flag is off, the shopper is logged in, or a number
 * is already on the cart. The gate then stays completely out of the way.
 *
 * There are two ways to reach checkout, and both are gated:
 *   1. The cart page's form submit whose button is name=checkout — intercepted
 *      here directly.
 *   2. The cart drawer's "Proceed to checkout" button, which now goes straight
 *      to /checkout. cart-drawer.js calls window.CaratKinCheckoutGate.intercept()
 *      before navigating, and only proceeds when the gate says it may.
 *
 * The gate is rendered globally (layout/theme.liquid) so it works from the drawer
 * on any page, not only on the cart page.
 */

(() => {
  if (window.CaratKinCheckoutGate) return;

  const cfgEl = document.getElementById("ck-whatsapp-gate");
  let cfg = {};
  try {
    cfg = JSON.parse(cfgEl ? cfgEl.textContent : "{}");
  } catch {
    cfg = {};
  }

  const overlay = document.querySelector("[data-wa-gate]");
  const form = document.querySelector("[data-wa-form]");
  const input = document.querySelector("[data-wa-input]");
  const errorEl = document.querySelector("[data-wa-error]");
  const cancelBtn = document.querySelector("[data-wa-cancel]");

  let pendingProceed = null;
  let bypass = false;

  // A number is required only when the feature is on, the shopper is a guest,
  // one is not already on the cart, and the modal is actually present.
  const gateNeeded = () =>
    Boolean(cfg.enabled) && !cfg.loggedIn && !cfg.hasNumber && !!overlay && !!form && !!input;

  // Public API. Any checkout entry point calls intercept(proceed): it returns
  // true when it has taken over (modal opened — the caller must NOT proceed), or
  // false when checkout may continue right now. On a successful number save the
  // gate runs proceed() itself.
  window.CaratKinCheckoutGate = {
    intercept(proceed) {
      if (bypass || !gateNeeded()) return false;
      pendingProceed = typeof proceed === "function" ? proceed : null;
      openModal();
      return true;
    },
    get required() {
      return gateNeeded();
    },
  };

  // Accept a 10-digit Indian mobile, with or without a 91 / 0 prefix, and store
  // it in +91XXXXXXXXXX form.
  function normalise(raw) {
    let digits = String(raw == null ? "" : raw).replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
    else if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
    return /^[6-9]\d{9}$/.test(digits) ? `+91${digits}` : null;
  }

  function openModal() {
    if (errorEl) errorEl.textContent = "";
    overlay.hidden = false;
    document.body.classList.add("wa-gate-open");
    window.setTimeout(() => input.focus(), 40);
  }
  function closeModal() {
    if (overlay) overlay.hidden = true;
    document.body.classList.remove("wa-gate-open");
  }
  function cancel() {
    pendingProceed = null;
    closeModal();
  }

  // Nothing left to wire when the modal isn't rendered (feature disabled). The
  // API above still exists and its intercept() returns false, so callers proceed.
  if (!overlay || !form || !input) return;

  // The cart page's own "Proceed to secure checkout" is a real form submit.
  document.addEventListener(
    "submit",
    (event) => {
      const submitter = event.submitter;
      if (bypass || !submitter || submitter.name !== "checkout" || !gateNeeded()) return;
      event.preventDefault();
      window.CaratKinCheckoutGate.intercept(() => {
        // Re-submit through the original button so name=checkout is in the POST.
        bypass = true;
        if (submitter.form && submitter.form.requestSubmit) submitter.form.requestSubmit(submitter);
        else submitter.click();
      });
    },
    true
  );

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const number = normalise(input.value);
    if (!number) {
      if (errorEl) errorEl.textContent = cfg.errorText || "Enter a valid 10-digit WhatsApp number.";
      input.focus();
      return;
    }

    const submitBtn = form.querySelector("[data-wa-submit]");
    if (submitBtn) submitBtn.disabled = true;

    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      const res = await fetch(`${root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ attributes: { "WhatsApp Number": number } }),
      });
      if (!res.ok) throw new Error(`cart update failed: ${res.status}`);

      cfg.hasNumber = true; // don't prompt again this page load
      closeModal();
      const proceed = pendingProceed;
      pendingProceed = null;
      if (proceed) proceed();
    } catch (error) {
      console.error("[wa-gate]", error);
      if (errorEl) errorEl.textContent = cfg.failText || "Something went wrong. Please try again.";
      if (submitBtn) submitBtn.disabled = false;
    }
  });

  cancelBtn?.addEventListener("click", cancel);
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) cancel();
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !overlay.hidden) cancel();
  });
})();
